/**
 * The Records pages the AI builder writes into a portal's customization
 * (`records.views`, keyed by slug): one view per record type the creator chose
 * to show clients. Changes to the view shape are additive only.
 */
export const MAX_RECORDS_VIEWS = 10
export const MAX_RECORDS_FIELDS = 20
export const MAX_RELATED_LISTS = 5
export const MAX_LINK_FIELDS = 5
export const MAX_RECORDS_ACTIONS = 5
export const RECORDS_SCOPES = ['contact', 'company', 'portal'] as const
export const RECORDS_SORT_DIRECTIONS = ['asc', 'desc'] as const
/** A view of the contact itself ("My details"): one client's own details, shown as a card rather than a list. */
export const DETAILS_OBJECT_KEY = 'contact'

export type RecordsScope = (typeof RECORDS_SCOPES)[number]
export type RecordsSortDirection = (typeof RECORDS_SORT_DIRECTIONS)[number]

export interface RecordsView {
  readonly objectKey: string
  readonly slug: string
  readonly title: string
  readonly associationId: string
  readonly fields: readonly string[]
  readonly scope?: RecordsScope
  readonly emptyMessage?: string
  readonly editableFields?: readonly string[]
  readonly searchableFields?: readonly string[]
  readonly sortField?: string
  readonly sortDirection?: RecordsSortDirection
  /** Fields a client fills in to add a record; absent means clients can't add. */
  readonly creatableFields?: readonly string[]
  /** Lists on a record's page: records of another view linked to the open record. */
  readonly related?: readonly RecordsRelatedList[]
  /** Pick-a-record fields: links from this view's records to another page's records. */
  readonly linkFields?: readonly RecordsLinkField[]
  /** Creatable fields a client must fill in to add a record. */
  readonly requiredFields?: readonly string[]
  /** Clients may delete their own records. */
  readonly deletable?: boolean
  /** One-click buttons; each keeps its position in the published list, which rows name by index. */
  readonly actions?: readonly RecordsAction[]
  /** The creator's word for adding on this page, e.g. "Request a quote"; "Add new" otherwise. */
  readonly addLabel?: string
}

export interface RecordsLinkField {
  readonly associationId: string
  /** The Records page the picker lists records from. */
  readonly slug: string
  readonly label: string
  readonly multiple?: boolean
  readonly creatable?: boolean
  readonly editable?: boolean
  readonly required?: boolean
}

export interface RecordsAction {
  /** Position in the published `actions`, as the server's rows and the action route name it. */
  readonly index: number
  readonly label: string
  readonly confirm?: boolean
  /** Pressing it opens a page in a new tab (Pay: the invoice to pay). */
  readonly opens?: true
  /** A pay button in Stripe test mode: the page says to use the test card. */
  readonly mode?: 'test' | 'live'
}

export interface RecordsRelatedList {
  readonly slug: string
  readonly associationId: string
  readonly title?: string
  /** The creator's word for adding from the parent's page, e.g. "Buy" or "Book"; "Add new" otherwise. */
  readonly addLabel?: string
  /** A client may add at most one record here per parent record (the server refuses a second). */
  readonly onePerClient?: true
}

/**
 * The page's buttons: the server's own labels and indexes when it sends
 * them (newer middleware), each keeping the overlay's "Are you sure?" by
 * index; the overlay's buttons otherwise.
 */
export function withServerActions(
  overlay: readonly RecordsAction[] = [],
  server?: readonly { readonly index: number; readonly label: string; readonly opens?: boolean; readonly mode?: 'test' | 'live' }[]
): readonly RecordsAction[] {
  if (!server) return overlay
  return server.map(({ index, label, opens, mode }) => ({
    index,
    label,
    ...(overlay.find(action => action.index === index)?.confirm ? { confirm: true } : {}),
    ...(opens === true ? { opens: true as const } : {}),
    ...(mode === 'test' || mode === 'live' ? { mode } : {}),
  }))
}

/** The lists under a record's page (or My details): each related list's own view, under its heading. */
export function relatedViews(
  view: RecordsView,
  views: readonly RecordsView[]
): { view: RecordsView; heading: string; addLabel?: string; onePerClient?: true }[] {
  return (view.related ?? []).flatMap(link => {
    const child = views.find(v => v.slug === link.slug)
    if (!child) return []
    return [
      {
        view: child,
        heading: link.title ?? child.title,
        ...(link.addLabel ? { addLabel: link.addLabel } : {}),
        ...(link.onePerClient ? { onePerClient: true as const } : {}),
      },
    ]
  })
}

const SLUG = /^[a-z0-9]+(?:-[a-z0-9]+)*$/

/**
 * The customization's `records` section as a list of raw views: each `views` entry with its key as the
 * slug, slugs in `order` first and the rest in key order.
 */
export function recordsViewList(records: unknown): unknown[] {
  const { order, views } = asRecord(records)
  const bySlug = asRecord(views)
  const ordered = Array.isArray(order)
    ? order.filter((slug): slug is string => typeof slug === 'string' && Object.hasOwn(bySlug, slug))
    : []
  return [...new Set([...ordered, ...Object.keys(bySlug)])].map(slug => ({ ...asRecord(bySlug[slug]), slug }))
}

/** Views from the portal's customization `records` section; anything malformed is skipped, never guessed. */
export function recordsViewsOf(records: unknown): RecordsView[] {
  return parseRecordsViews(recordsViewList(records))
}

/** The page title a link field is named after when the creator gave it no label. */
type TitleOf = (slug: string) => string | undefined

/**
 * At most ten views; a view that reuses an earlier slug is dropped so a slug
 * always names one page. Keys it doesn't know (e.g. the server's `filters`) are ignored.
 */
export function parseRecordsViews(value: unknown): RecordsView[] {
  if (!Array.isArray(value)) return []
  const parse = (titleOf: TitleOf) => {
    const slugs = new Set<string>()
    return value.slice(0, MAX_RECORDS_VIEWS).flatMap(raw => {
      const view = parseRecordsView(raw, titleOf)
      if (!view || slugs.has(view.slug)) return []
      slugs.add(view.slug)
      return [view]
    })
  }
  // Which pages exist never depends on link labels, so a first pass names them for the second.
  const titles = new Map(parse(() => undefined).map(view => [view.slug, view.title]))
  return parse(slug => titles.get(slug))
}

function parseRecordsView(raw: unknown, titleOf: TitleOf): RecordsView | undefined {
  const view = asRecord(raw)
  const { objectKey, slug, title, associationId, scope, emptyMessage, sortField, sortDirection, addLabel } = view
  const fields = stringList(view.fields)
  if (!fields?.length || !isText(objectKey, 1, Infinity) || !isText(associationId, 1, Infinity)) return undefined
  if (!isText(title, 1, 60) || !isText(slug, 1, 40) || !SLUG.test(slug)) return undefined
  // Optional settings that do not fit are dropped, which is always the narrower reading (no search, no edits).
  const subset = (list: unknown) => stringList(list)?.filter(key => fields.includes(key))
  const editableFields = subset(view.editableFields)
  const searchableFields = subset(view.searchableFields)
  // Adding, links and delete are for a client's own records: never portal-wide lists, never the contact itself.
  // Buttons also show on portal-wide lists (Buy on a catalog item); the server lists which apply to each row.
  const ownRecords = scope !== 'portal' && objectKey !== DETAILS_OBJECT_KEY
  const creatableFields = ownRecords ? subset(view.creatableFields) : undefined
  const related = relatedLists(view.related, slug)
  const requiredFields = creatableFields && stringList(view.requiredFields)?.filter(key => creatableFields.includes(key))
  const linkFields = ownRecords ? linkFieldsOf(view.linkFields, slug, titleOf) : []
  const actions = objectKey !== DETAILS_OBJECT_KEY ? actionsOf(view.actions) : []
  return {
    objectKey,
    slug,
    title,
    associationId,
    fields,
    ...(RECORDS_SCOPES.includes(scope as RecordsScope) ? { scope: scope as RecordsScope } : {}),
    ...(isText(emptyMessage, 1, 200) ? { emptyMessage } : {}),
    ...(editableFields ? { editableFields } : {}),
    ...(searchableFields ? { searchableFields } : {}),
    ...(creatableFields?.length ? { creatableFields } : {}),
    ...(related.length ? { related } : {}),
    ...(linkFields.length ? { linkFields } : {}),
    ...(requiredFields?.length ? { requiredFields } : {}),
    ...(ownRecords && view.deletable === true ? { deletable: true } : {}),
    ...(actions.length ? { actions } : {}),
    ...(isText(addLabel, 1, 40) ? { addLabel } : {}),
    ...(typeof sortField === 'string' && fields.includes(sortField) ? { sortField } : {}),
    ...(RECORDS_SORT_DIRECTIONS.includes(sortDirection as RecordsSortDirection)
      ? { sortDirection: sortDirection as RecordsSortDirection }
      : {}),
  }
}

/** At most five related lists; one naming its own view is dropped. */
function relatedLists(value: unknown, ownSlug: string): RecordsRelatedList[] {
  if (!Array.isArray(value)) return []
  return value.slice(0, MAX_RELATED_LISTS).flatMap(raw => {
    const { slug, associationId, title, addLabel, onePerClient } = asRecord(raw)
    if (!isText(slug, 1, 40) || !SLUG.test(slug) || slug === ownSlug || !isText(associationId, 1, Infinity)) return []
    return [
      {
        slug,
        associationId,
        ...(isText(title, 1, 60) ? { title } : {}),
        ...(isText(addLabel, 1, 30) ? { addLabel } : {}),
        ...(onePerClient === true ? { onePerClient } : {}),
      },
    ]
  })
}

/** At most five link fields, each to another page. One without a label is named as the server names it. */
function linkFieldsOf(value: unknown, ownSlug: string, titleOf: TitleOf): RecordsLinkField[] {
  if (!Array.isArray(value)) return []
  return value.slice(0, MAX_LINK_FIELDS).flatMap(raw => {
    const link = asRecord(raw)
    const { associationId, slug } = link
    if (!isText(associationId, 1, Infinity) || !isText(slug, 1, 40) || !SLUG.test(slug) || slug === ownSlug) return []
    const label = link.label === undefined ? (titleOf(slug) ?? slug) : link.label
    if (!isText(label, 1, 60)) return []
    const flags = (['multiple', 'creatable', 'editable', 'required'] as const).filter(flag => link[flag] === true)
    return [{ associationId, slug, label, ...Object.fromEntries(flags.map(flag => [flag, true])) }]
  })
}

/**
 * At most five buttons. A malformed one is skipped but never renumbers the rest: rows name buttons by position.
 * A set button writes `value` into `field`; a pay button charges the Money field `pay` names and opens the invoice.
 */
function actionsOf(value: unknown): RecordsAction[] {
  if (!Array.isArray(value)) return []
  return value.slice(0, MAX_RECORDS_ACTIONS).flatMap((raw, index) => {
    const { label, field, value: target, pay, confirm } = asRecord(raw)
    const opens = isText(pay, 1, Infinity)
    if (!isText(label, 1, 60) || (!opens && (!isText(field, 1, Infinity) || typeof target !== 'string'))) return []
    return [{ index, label, ...(confirm === true ? { confirm: true } : {}), ...(opens ? { opens: true as const } : {}) }]
  })
}

function stringList(value: unknown): string[] | undefined {
  return Array.isArray(value) && value.length <= MAX_RECORDS_FIELDS && value.every(item => typeof item === 'string') ? value : undefined
}

function isText(value: unknown, min: number, max: number): value is string {
  return typeof value === 'string' && value.length >= min && value.length <= max
}

function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value) ? (value as Record<string, unknown>) : {}
}
